/**
 * Video Editor — operation catalog + FFmpeg argument builder.
 *
 * This module is the source of truth for what the Video Editor can do. Each
 * operation describes:
 *   - its identity (id, label, description, category)
 *   - the typed inputs it needs (video file slots + parameter fields)
 *   - how those values map to an FFmpeg argv array
 *
 * The same catalog feeds three independent consumers:
 *   1. The Video Editor hero page (renders the picker + form per operation)
 *   2. The dispatch service (spawns FFmpeg with `buildFfmpegArgs(...)`)
 *   3. The AI-agent `video_editor` dispatch mode (turns the input schema into
 *      an Output Contract + auto-generated system prompt — same pattern as
 *      direct_provider on Visuals models).
 *
 * Adding a new operation = add an entry to VIDEO_EDITOR_OPERATIONS plus a
 * case in buildFfmpegArgs. Nothing else in the codebase needs to change.
 */
// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------
export const VIDEO_EDITOR_OPERATIONS = [
    {
        id: 'trim',
        label: 'Trim / Shorten',
        description: 'Cut a video to a time range. Stream-copies the source when codecs allow ' +
            '(fast, no re-encode); falls back to a clean re-encode otherwise.',
        category: 'single_input',
        capabilities: ['Single input', 'Fast (no re-encode)'],
        inputs: [
            {
                name: 'video',
                kind: 'video',
                label: 'Source video',
                required: true,
            },
            {
                name: 'startSec',
                kind: 'time',
                label: 'Start time',
                description: 'Trim starts at this point in the source.',
                required: true,
                defaultValue: 0,
            },
            {
                name: 'endSec',
                kind: 'time',
                label: 'End time',
                description: 'Trim ends here. Must be after the start time.',
                required: true,
            },
        ],
        outputMimeType: 'video/mp4',
        outputExtension: 'mp4',
    },
    {
        id: 'merge',
        label: 'Merge Videos',
        description: 'Concatenate two or more videos in order into a single output. ' +
            'Re-encodes to a common codec when the inputs disagree.',
        category: 'multi_input',
        capabilities: ['Multi-input', 'Re-encodes'],
        inputs: [
            {
                name: 'videos',
                kind: 'videos',
                label: 'Videos to merge (in order)',
                description: 'Drag to reorder. Minimum 2.',
                required: true,
            },
        ],
        outputMimeType: 'video/mp4',
        outputExtension: 'mp4',
    },
    {
        id: 'crop',
        label: 'Crop',
        description: 'Cut a rectangular region out of every frame. Pairs well with Resize ' +
            'when reframing 16:9 → 9:16 short-form vertical.',
        category: 'single_input',
        capabilities: ['Single input', 'Re-encodes'],
        inputs: [
            {
                name: 'video',
                kind: 'video',
                label: 'Source video',
                required: true,
            },
            {
                name: 'rect',
                kind: 'rect',
                label: 'Crop region',
                description: 'Pixel coordinates relative to the source frame.',
                required: true,
            },
        ],
        outputMimeType: 'video/mp4',
        outputExtension: 'mp4',
    },
    {
        id: 'resize',
        label: 'Resize / Change Aspect Ratio',
        description: 'Scale to a target resolution. Letterbox keeps every pixel of the ' +
            'source visible; Crop-to-fit fills the frame and trims overflow.',
        category: 'single_input',
        capabilities: ['Single input', 'Re-encodes'],
        inputs: [
            {
                name: 'video',
                kind: 'video',
                label: 'Source video',
                required: true,
            },
            {
                name: 'preset',
                kind: 'enum',
                label: 'Target preset',
                required: true,
                defaultValue: '1080p_landscape',
                choices: [
                    { value: '1080p_landscape', label: '1080p Landscape (1920×1080)' },
                    { value: '720p_landscape', label: '720p Landscape (1280×720)' },
                    { value: '1080p_square', label: '1080×1080 Square' },
                    { value: '1080p_vertical', label: '1080×1920 Vertical (9:16 short)' },
                    { value: '720p_vertical', label: '720×1280 Vertical (9:16 short)' },
                ],
            },
            {
                name: 'fitMode',
                kind: 'enum',
                label: 'Fit mode',
                description: 'How to handle aspect-ratio mismatch.',
                required: true,
                defaultValue: 'letterbox',
                choices: [
                    { value: 'letterbox', label: 'Letterbox (pad with black bars)' },
                    { value: 'crop', label: 'Crop to fit (trim overflow)' },
                    { value: 'stretch', label: 'Stretch (distorts image)' },
                ],
            },
        ],
        outputMimeType: 'video/mp4',
        outputExtension: 'mp4',
    },
];
export function findVideoEditorOperation(id) {
    return VIDEO_EDITOR_OPERATIONS.find((op) => op.id === id);
}
const RESIZE_PRESET_DIMS = {
    '1080p_landscape': { w: 1920, h: 1080 },
    '720p_landscape': { w: 1280, h: 720 },
    '1080p_square': { w: 1080, h: 1080 },
    '1080p_vertical': { w: 1080, h: 1920 },
    '720p_vertical': { w: 720, h: 1280 },
};
export function buildFfmpegArgs(input) {
    switch (input.operationId) {
        case 'trim':
            return buildTrimArgs(input);
        case 'merge':
            return buildMergeArgs(input);
        case 'crop':
            return buildCropArgs(input);
        case 'resize':
            return buildResizeArgs(input);
    }
}
function buildTrimArgs(input) {
    const [src] = input.inputPaths;
    if (!src)
        throw new Error('trim: missing input video');
    const startSec = numberValue(input.values, 'startSec', 0) ?? 0;
    const endSec = numberValue(input.values, 'endSec');
    if (endSec === undefined)
        throw new Error('trim: endSec is required');
    if (endSec <= startSec)
        throw new Error('trim: endSec must be greater than startSec');
    const duration = endSec - startSec;
    // Stream-copy first; the dispatch service can retry with a re-encode if
    // ffmpeg complains about the codec.
    return [
        '-y',
        '-ss',
        String(startSec),
        '-i',
        src,
        '-t',
        String(duration),
        '-c',
        'copy',
        '-movflags',
        '+faststart',
        input.outputPath,
    ];
}
/**
 * Merge uses ffmpeg's concat demuxer via a generated listfile that the
 * dispatch service writes to disk alongside the inputs. The listfile path
 * is passed as the SECOND positional after the input list — callers are
 * expected to set inputPaths = [<listfilePath>] for merge specifically.
 */
function buildMergeArgs(input) {
    const [listfile] = input.inputPaths;
    if (!listfile)
        throw new Error('merge: missing concat listfile path');
    return [
        '-y',
        '-f',
        'concat',
        '-safe',
        '0',
        '-i',
        listfile,
        // Re-encode for safety — concat demuxer requires identical codecs in
        // stream-copy mode, which we can't guarantee across user uploads.
        '-c:v',
        'libx264',
        '-preset',
        'veryfast',
        '-crf',
        '23',
        '-c:a',
        'aac',
        '-b:a',
        '192k',
        '-movflags',
        '+faststart',
        input.outputPath,
    ];
}
function buildCropArgs(input) {
    const [src] = input.inputPaths;
    if (!src)
        throw new Error('crop: missing input video');
    const rect = input.values['rect'];
    if (!rect || typeof rect !== 'object')
        throw new Error('crop: rect is required');
    if (rect.width <= 0 || rect.height <= 0) {
        throw new Error('crop: rect width/height must be positive');
    }
    // ffmpeg crop filter: crop=w:h:x:y
    const filter = `crop=${Math.floor(rect.width)}:${Math.floor(rect.height)}:${Math.floor(rect.x)}:${Math.floor(rect.y)}`;
    return [
        '-y',
        '-i',
        src,
        '-vf',
        filter,
        '-c:v',
        'libx264',
        '-preset',
        'veryfast',
        '-crf',
        '23',
        '-c:a',
        'copy',
        '-movflags',
        '+faststart',
        input.outputPath,
    ];
}
function buildResizeArgs(input) {
    const [src] = input.inputPaths;
    if (!src)
        throw new Error('resize: missing input video');
    const preset = input.values['preset'] ?? '1080p_landscape';
    const fitMode = input.values['fitMode'] ?? 'letterbox';
    const dims = RESIZE_PRESET_DIMS[preset];
    if (!dims)
        throw new Error(`resize: unknown preset "${preset}"`);
    let filter;
    switch (fitMode) {
        case 'letterbox':
            // Scale to fit inside dims preserving aspect, then pad to dims.
            filter =
                `scale=w=${dims.w}:h=${dims.h}:force_original_aspect_ratio=decrease,` +
                    `pad=${dims.w}:${dims.h}:(ow-iw)/2:(oh-ih)/2:black`;
            break;
        case 'crop':
            // Scale to cover dims preserving aspect, then crop the overflow.
            filter =
                `scale=w=${dims.w}:h=${dims.h}:force_original_aspect_ratio=increase,` +
                    `crop=${dims.w}:${dims.h}`;
            break;
        case 'stretch':
            // Plain anamorphic stretch.
            filter = `scale=${dims.w}:${dims.h}`;
            break;
    }
    return [
        '-y',
        '-i',
        src,
        '-vf',
        filter,
        '-c:v',
        'libx264',
        '-preset',
        'veryfast',
        '-crf',
        '23',
        '-c:a',
        'copy',
        '-movflags',
        '+faststart',
        input.outputPath,
    ];
}
function numberValue(values, key, fallback) {
    const v = values[key];
    if (typeof v === 'number' && Number.isFinite(v))
        return v;
    return fallback;
}
// ---------------------------------------------------------------------------
// AI-agent integration — mirrors visuals-generator's `buildDirectProvider*`.
//
// When an admin picks dispatchMode='video_editor' on an agent, the system:
//   1. Uses buildVideoEditorOutputSchema(op) as the JSON schema the agent
//      must emit (the Output Contract).
//   2. Auto-generates the agent's systemPrompt via
//      buildVideoEditorSystemPrompt(op).
//   3. Routes the confirmed proposal to the dispatch service with the typed
//      values + the user's attached video(s) as inputs.
// ---------------------------------------------------------------------------
export function buildVideoEditorOutputSchema(op) {
    const properties = {};
    const required = [];
    for (const input of op.inputs) {
        // File inputs aren't fields the agent fills in; the user attaches them
        // in chat. Skip them from the schema (the dispatch service pairs them
        // up with the agent's collected values at confirm time).
        if (input.kind === 'video' || input.kind === 'videos')
            continue;
        properties[input.name] = inputToJsonSchema(input);
        if (input.required)
            required.push(input.name);
    }
    return {
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        title: `${op.label} — input contract`,
        description: op.description,
        properties,
        required,
        additionalProperties: false,
    };
}
function inputToJsonSchema(input) {
    switch (input.kind) {
        case 'time':
            return {
                type: 'number',
                description: `${input.label} in seconds. Accepts decimals.`,
                minimum: 0,
            };
        case 'number': {
            const out = { type: 'number', description: input.label };
            if (typeof input.min === 'number')
                out['minimum'] = input.min;
            if (typeof input.max === 'number')
                out['maximum'] = input.max;
            return out;
        }
        case 'enum':
            return {
                type: 'string',
                description: input.label,
                enum: (input.choices ?? []).map((c) => c.value),
            };
        case 'rect':
            return {
                type: 'object',
                description: `${input.label}: pixel coordinates {x,y,width,height}.`,
                properties: {
                    x: { type: 'number', minimum: 0 },
                    y: { type: 'number', minimum: 0 },
                    width: { type: 'number', minimum: 1 },
                    height: { type: 'number', minimum: 1 },
                },
                required: ['x', 'y', 'width', 'height'],
                additionalProperties: false,
            };
        default:
            return { type: 'string', description: input.label };
    }
}
export function buildVideoEditorSystemPrompt(op) {
    const lines = [];
    lines.push(`You are an AI assistant that helps users ${op.label.toLowerCase()} videos via the portal's built-in FFmpeg video editor.`);
    lines.push('');
    lines.push(op.description);
    lines.push('');
    lines.push(`Chat naturally with the user, collect the inputs the operation needs, recap them, and ask the user to confirm before emitting the proposal JSON.`);
    lines.push('');
    lines.push('# What the operation needs');
    const videoInputs = op.inputs.filter((i) => i.kind === 'video' || i.kind === 'videos');
    const paramInputs = op.inputs.filter((i) => i.kind !== 'video' && i.kind !== 'videos');
    for (const i of videoInputs) {
        if (i.kind === 'videos') {
            lines.push(`- ${i.label} — at least 2 videos. The user attaches them in chat; you don't put videos in the JSON.`);
        }
        else {
            lines.push(`- ${i.label} — the source video. The user attaches it in chat; you don't put it in the JSON.`);
        }
    }
    for (const i of paramInputs) {
        let line = `- ${i.name} (${i.kind})`;
        if (i.required)
            line += ', required';
        line += ` — ${i.label}.`;
        if (i.description)
            line += ` ${i.description}`;
        if (i.kind === 'enum' && i.choices) {
            line += ` Options: ${i.choices.map((c) => c.value).join(', ')}.`;
        }
        lines.push(line);
    }
    lines.push('');
    lines.push('# How to chat');
    lines.push('- Open with a short greeting and ask what they want to do. Keep it warm and concise.');
    lines.push('- Ask follow-up questions ONE AT A TIME. Never dump a form on the user.');
    if (videoInputs.length > 0) {
        lines.push('- If the user hasn\'t attached a video yet, ask them to drop one into chat before continuing.');
    }
    lines.push('');
    lines.push('# Confirmation');
    lines.push('When you have everything, summarize the collected inputs back to the user in a short, scannable recap and ask: "Looks good? Reply confirm to render, or tell me what to change."');
    lines.push('If they confirm, emit the proposal JSON (see the Output Contract section below). If they ask for changes, update the relevant field, recap again, and re-confirm before emitting.');
    lines.push('');
    lines.push('After you emit the JSON, the system handles dispatch to FFmpeg and renders the result inline as soon as it\'s ready — you don\'t need to do anything else.');
    return lines.join('\n');
}
//# sourceMappingURL=video-editor.js.map